import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { isEmpty } from '@ember/utils'
import { addDays } from 'date-fns'
import { ThemeKeys, subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'
import type BookLibraryCollectionService from 'teacher-ui-client/services/book-library/collection'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type RouterService from '@ember/routing/router-service'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type Student from 'teacher-ui-client/models/student'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { t } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { ShareCollectionTable } from 'teacher-ui-client/components/book-library/share-collection-table'
import { BookLibraryShareExpiryTile } from 'teacher-ui-client/components/book-library/share-expiry-tile'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import type { RowId } from 'teacher-ui-client/components/stable-table/basic-table/cell'

interface Args {
  subscriptionType: SubscriptionType
  collection: BookLibraryCollection
  students: Student[]
  gradePosition?: number
  schoolClassId?: string
  searchString?: string
  selectedStudentIds?: string[]
  sortingConfig?: SortingConfig
  paginationConfig?: PaginationConfig
  onSortClick?: (sortingConfig: SortingConfig) => void
  onSelectClick?: (studentsId: RowId[]) => void
  onSearchInput?: (value: string) => void
  onGradePositionSelect?: (gradePosition: number) => void
  onSchoolClassIdSelect?: (schoolClassId: string) => void
}
interface Signature {
  Args: Args
}

export interface Theme {
  panelHeaderBgColor?: string
  panelHeaderIconColor?: string
}

const themes: Partial<Record<ThemeKeys, Theme>> = {
  [ThemeKeys.blue]: { panelHeaderBgColor: 'bg-oceany-blue-150', panelHeaderIconColor: 'text-oceany-blue-325' },
  [ThemeKeys.green]: { panelHeaderBgColor: 'bg-ms-green-150', panelHeaderIconColor: 'text-ms-green-325' },
  [ThemeKeys.default]: { panelHeaderBgColor: 'bg-dusty-black-100', panelHeaderIconColor: 'text-black' },
}

export class BookLibrarySharesNew extends Component<Signature> {
  @service('book-library/collection')
  collectionService!: BookLibraryCollectionService

  @service
  router!: RouterService

  @service
  intl!: IntlService

  @service
  flashQueue!: FlashQueueService

  @service
  log!: Log

  @tracked startDate = new Date()

  @tracked endDate: Date = addDays(new Date(), 7)

  get themeKey() {
    return subscriptionTypeToThemeKey(this.args.subscriptionType)
  }

  get selectedTheme() {
    return themes[this.themeKey] ?? themes[ThemeKeys.default]
  }

  get disableSubmit() {
    return (
      this.collectionService.shareWithStudents.isRunning ||
      isEmpty(this.args.selectedStudentIds) ||
      !this.startDate ||
      !this.endDate
    )
  }

  @action
  onDateSelect({ startDate, endDate }: { startDate: Date; endDate?: Date | null }) {
    this.startDate = startDate
    if (endDate) {
      this.endDate = endDate
    }
  }

  @action
  async shareCollection(collection: BookLibraryCollection, selectedStudentIds: string[] = []) {
    const students = this.args.students.filter((student) => student.id && selectedStudentIds.includes(student.id))

    try {
      if (isEmpty(students)) throw new Error('no students selected')
      if (!this.endDate) throw new Error('no end date selected')
      await this.collectionService.shareWithStudents.perform(collection, students, this.startDate, this.endDate)
      this.onSuccess({ recipientCount: selectedStudentIds.length })
    } catch (error) {
      this.onError(error)
    }
  }

  @action
  onSuccess({ recipientCount }: { recipientCount: number }) {
    const message = this.intl.t('pages.bookLibrary.shares.new.success', { count: recipientCount || 0, htmlSafe: true })
    this.flashQueue.addSuccess({ subtitle: message })
    void this.router.transitionTo('subscription-type.book-library.collection.index')
  }

  @action
  onError(error?: any) {
    this.log.error('failed to share collection', error)
    const message = this.intl.t('pages.bookLibrary.shares.new.error', { htmlSafe: true })
    this.flashQueue.addFail({ subtitle: message })
  }

  <template>
    <div class="space-y-6">
      <div class="space-y-6 text-center" data-test-share-collection-header>
        <div>
          <h2>{{t "pages.bookLibrary.shares.new.title" name=@collection.name}}</h2>
          <span class="text-dusty-black-200 block">
            ({{t "bookLibrary.bookCount" count=@collection.count}})
          </span>
        </div>
        <p>{{t "pages.bookLibrary.shares.new.selectStudents"}}</p>
        <p>{{t "pages.bookLibrary.shares.new.explanation"}}</p>
      </div>
      <ContentTile data-test-share-collection-student-panel>
        <div class="flex items-center p-4 {{this.selectedTheme.panelHeaderBgColor}}">
          <span class="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-white">
            <PhosphorIcon
              @icon="phosphor-users-three-fill"
              class="{{this.selectedTheme.panelHeaderIconColor}} h-7 w-7"
            />
          </span>
          <h3>{{t "pages.bookLibrary.shares.new.studentsPanel.header"}}</h3>
        </div>
        <ShareCollectionTable
          @students={{@students}}
          @subscriptionType={{@subscriptionType}}
          @themeKey={{this.themeKey}}
          @sortingConfig={{@sortingConfig}}
          @paginationConfig={{@paginationConfig}}
          @selectedStudentIds={{@selectedStudentIds}}
          @searchString={{@searchString}}
          @gradePosition={{@gradePosition}}
          @schoolClassId={{@schoolClassId}}
          @onSortClick={{@onSortClick}}
          @onSelectClick={{@onSelectClick}}
          @onSearchInput={{@onSearchInput}}
          @onGradePositionSelect={{@onGradePositionSelect}}
          @onSchoolClassIdSelect={{@onSchoolClassIdSelect}}
        />
      </ContentTile>
      <BookLibraryShareExpiryTile
        data-test-share-collection-expiry-panel
        @themeKey={{this.themeKey}}
        @selectedTheme={{this.selectedTheme}}
        @startDate={{this.startDate}}
        @endDate={{this.endDate}}
        @onDateSelect={{this.onDateSelect}}
      />
      <div class="text-right">
        <UiButton
          data-test-share-collection-submit
          type="button"
          disabled={{this.disableSubmit}}
          @appearance={{ButtonAppearance.regular}}
          {{on "click" (fn this.shareCollection @collection @selectedStudentIds)}}
        >
          {{t "pages.bookLibrary.shares.new.shareButtonLabel"}}
        </UiButton>
      </div>
    </div>
  </template>
}

export default BookLibrarySharesNew
